import React, { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import UserProfileDropdown from "../../components/common/UserProfileDropdown";
import {
  Home,
  Package,
  Users,
  ShoppingBag,
  Building2,
  Menu,
  X,
  ChevronDown,
  Receipt,
} from "lucide-react";
interface AdminLayoutProps {
  children: React.ReactNode;
  title?: string;
}
const AdminLayout: React.FC<AdminLayoutProps> = ({
  children,
  title = "Admin Dashboard",
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const navigationItems = [
    ...(user?.role === "admin"
      ? [
          {
            name: "Dashboard",
            href: "/admin/dashboard",
            icon: Home,
            current: location.pathname === "/admin/dashboard",
          },
          {
            name: "Products",
            href: "/admin/products",
            icon: Package,
            current: location.pathname === "/admin/products",
          },
          {
            name: "Users",
            href: "/admin/users",
            icon: Users,
            current: location.pathname === "/admin/users",
          },
          {
            name: "Orders",
            href: "/admin/orders",
            icon: ShoppingBag,
            current: location.pathname === "/admin/orders",
          },
          {
            name: "Lockbox Invoices",
            href: "/admin/invoices",
            icon: Receipt,
            current: location.pathname === "/admin/invoices",
          },
          {
            name: "Accounts",
            href: "/admin/accounts",
            icon: Building2,
            current: location.pathname.startsWith("/admin/accounts"),
          },
        ]
      : []),
    ...(user?.role === "clinic"
      ? [
          {
            name: "Dashboard",
            href: "/account/dashboard",
            icon: Home,
            current: location.pathname === "/account/dashboard",
          },
          {
            name: "Orders",
            href: "/account/orders",
            icon: ShoppingBag,
            current: location.pathname === "/account/orders",
          },
          {
            name: "Accounts",
            href: "/account/accounts",
            icon: Building2,
            current: location.pathname.startsWith("/account/accounts"),
          },
        ]
      : []),
  ];
  const handleNavigation = (href: string) => {
    navigate(href);
    setIsSidebarOpen(false);
  };
  return (
    <div className="min-h-screen w-full bg-gray-50 flex overflow-hidden">
      {/* Mobile sidebar backdrop */}
      {isSidebarOpen && (
        <div
          className="fixed inset-0 bg-gray-600 bg-opacity-75 z-20 lg:hidden"
          onClick={() => setIsSidebarOpen(false)}
        />
      )}
      {/* Sidebar */}
      <div
        className={`${
          isSidebarOpen ? "translate-x-0" : "-translate-x-full"
        } fixed inset-y-0 left-0 z-30 w-64 bg-white shadow-lg transform transition-transform duration-300 ease-in-out lg:translate-x-0 flex flex-col`}
      >
        <div className="flex items-center justify-between h-16 px-6 border-b border-gray-200">
          <div className="flex items-center">
            <div className="h-8 w-8 rounded-lg flex items-center justify-center">
              <img
                src="/assets/logo.svg"
                alt="Gattaca Genomics Logo"
                className="h-full w-full"
              />
            </div>
            <span className="ml-3 text-lg font-semibold text-gray-900">
              Marketplace
            </span>
          </div>
          <button
            onClick={() => setIsSidebarOpen(false)}
            className="lg:hidden text-gray-500 hover:text-gray-700"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        <nav className="flex-1 mt-8 px-4 overflow-y-auto">
          <ul className="space-y-2 pb-4 min-h-fit">
            {navigationItems.map((item) => {
              const Icon = item.icon;
              return (
                <li key={item.name}>
                  <button
                    onClick={() => handleNavigation(item.href)}
                    className={`w-full flex items-center px-4 py-3 text-sm font-medium rounded-lg transition-colors ${
                      item.current
                        ? "bg-blue-50 text-blue-700 border-l-2 border-blue-700 rounded-none"
                        : "text-gray-700 hover:bg-gray-50 hover:text-gray-900"
                    }`}
                  >
                    <Icon className="mr-3 h-5 w-5 flex-shrink-0" />
                    {item.name}
                  </button>
                </li>
              );
            })}
          </ul>
        </nav>

        {/* User info at bottom */}
        <div className="p-4 border-t border-gray-200 flex-shrink-0">
          <div className="flex items-center">
            <div className="h-8 w-8 bg-blue-100 rounded-full flex items-center justify-center">
              <span className="text-sm font-medium text-blue-700">
                {user?.first_name?.[0]?.toUpperCase() || "A"}
              </span>
            </div>
            <div className="ml-3 flex-1 min-w-0">
              <p className="text-sm font-medium text-gray-700 truncate">
                {user?.first_name} {user?.last_name}
              </p>
              <p className="text-xs text-gray-500 truncate">{user?.email}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Main content */}
      <div className="flex-1 flex flex-col min-w-0 lg:ml-64">
        {/* Top header */}
        <header className="bg-gray-50  fixed top-0 right-0 left-0 lg:left-64 z-10">
          <div className="flex items-center justify-between h-16 px-4 sm:px-6 lg:px-8">
            <div className="flex items-center">
              <button
                onClick={() => setIsSidebarOpen(true)}
                className="lg:hidden p-2 rounded-md text-gray-400 hover:text-gray-500 hover:bg-gray-100"
              >
                <Menu className="h-6 w-6" />
              </button>
              <h1
                style={{
                  width: "max-content",
                }}
                className="ml-2 lg:ml-0 text-base lg:text-xl font-semibold text-gray-900"
              >
                {title}
              </h1>
            </div>

            <div className="flex items-center space-x-4 w-full justify-end">
              <UserProfileDropdown />
            </div>
          </div>
        </header>

        {/* Page content */}
        <main className="flex-1 overflow-y-auto overflow-x-hidden mt-16">
          <div className="py-6 min-h-full">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 min-w-0">
              {children}
            </div>
          </div>
        </main>
      </div>
    </div>
  );
};
export default AdminLayout;
